import type { DefaultSocketResponseType, ErrorCode } from '../../../../services/socket/SocketEvents';
import { SectionEvents } from '../../../../services/socket/SocketEvents';
import { AcknowledgmentResponseStatus } from '../../../../services/socket/SocketEvents';
import { BlockEvents, SignatureEvents } from '../../../../services/socket/SocketEvents';
import type { Block } from 'components/editor/GridDndEditor/models/Block.model';
import type SocketClient from '../../../../services/socket/SocketClient';
import type { SignaturePropertyUpdate, SignatureReassignPayload } from '../../../../services/socket/SocketClient';
import type { GridAddTableType, SaveTableContentType, TableType } from './table.types';
import { assertCrmRowMarkersValid } from './table.types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { ContentType } from '../../../../utils/getContentType.ts';
import { GridBlockType } from 'services/entities/BlockTypes';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import { sanitizeAssignedSignee, sanitizeAssignedSigneeForReassign } from 'services/entities/SignatureTypes';
import type { SectionContentType } from '../../Sections/Section.model.ts';
import type { BlockSettings } from 'services/entities/BlockTypes';

export type GridDefaultType = {
  blockId?: string;
  userId: number;
};

export type GridDeleteType = {
  blockId: string;
};

export type GridContentChangedType = GridDefaultType & {
  htmlContent: string;
  tableContent?: TableType;
};

export type GridContentObjectChangedType = GridDefaultType & {
  content: Record<string, unknown>;
};

export type GridPositionChangedType = GridDefaultType & {
  position: {
    top_px: number;
    left_px: number;
  };
};

export type GridDimensionChangedType = GridDefaultType & {
  dimensions: {
    width_px: number;
    height_px: number;
  };
};

export type GridLayerChangedType = GridDefaultType & {
  zIndex: number;
};

export type GridAddType = {
  blockId: string;
  type: GridBlockType;
} & GridContentChangedType &
  Partial<GridContentObjectChangedType> &
  GridPositionChangedType &
  GridLayerChangedType &
  GridDimensionChangedType & { sectionId: string };

export type SocketBlockSettings = {
  updatedByUserId: number;
  blockId: string;
  lockedBy?: number | null;
  borderLeft: number;
  borderRight: number;
  borderTop: number;
  borderBottom: number;
  borderColor: string;
  borderRadius: number;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  paddingBottom: number;
  currentTimestamp: string;
  backgroundColor?: string;
  imageAlt?: string;
  imageLink?: string;
  opacity?: number;
  imageAspectRatioLock?: boolean;
  top?: number;
};

export interface BlockPosition {
  top_px: number;
  left_px: number;
}

export interface BlockDimension {
  width_px: number;
  height_px: number;
}

export interface BlockAddedData {
  blockId: string;
  sectionId: string;
  userId: number;
  type: GridBlockType.TEXT | GridBlockType.IMAGE | GridBlockType.VIDEO | GridBlockType.SHAPE | GridBlockType.EMBED;
  htmlContent: string;
  zIndex?: number;
  position: BlockPosition;
  dimensions: BlockDimension;
  content?: ContentType;
  styles?: BlockSettings;
}

export interface BlockTableAddedData {
  sectionId: string;
  userId: number;
  blockId: string;
  type: GridBlockType.TABLE;
  content: TableType;
  zIndex?: number;
  position: BlockPosition;
  dimensions: BlockDimension;
}

export type GridNewAddType = BlockAddedData | BlockTableAddedData | BlockEmbedAddedData;

export type GridLoadType = GridAddType & {
  blockSettings: BlockSettings;
};

export type GridAddedSocketResponseType = DefaultSocketResponseType & { content: { blockId: string } };
type GridDefaultSocketCallbackType = (response: DefaultSocketResponseType) => void;
type GridAddedSocketCallbackType = (response: GridAddedSocketResponseType) => void;
type SaveLayerWithSocketPayloadType = {
  gridBlockId: string;
  zIndex: number;
  userId: number;
  socketClient: SocketClient;
};

export type BlockEmbedAddedData = {
  blockId: string;
  sectionId: string;
  userId: number;
  type: GridBlockType.EMBED;
  position: BlockPosition;
  dimensions: BlockDimension;
  zIndex?: number;
  content?: Record<string, never> | null;
  styles?: BlockSettings;
};

const createSocketPromise = <T>(socketClient: SocketClient, event: BlockEvents, data: any, callback: (response: T) => void): Promise<T> =>
  new Promise<T>((resolve) => {
    socketClient.publish(event, data, (response: T) => {
      callback(response);
      resolve(response);
    });
  });

export const addGridBlockWithSocket = createAsyncThunk(
  'editor-grid-block/saveContentWithSocket',
  async ({
    content,
    blockConfig,
    socketClient,
    userId,
    callback,
    type,
    sectionId,
  }: {
    content: string;
    blockConfig: Block;
    socketClient: SocketClient;
    userId: number;
    callback: GridAddedSocketCallbackType;
    type: GridBlockType;
    sectionId: string;
  }) => {
    const data: GridAddType = {
      blockId: uuidv4(),
      userId,
      position: { top_px: blockConfig.y, left_px: blockConfig.x },
      zIndex: blockConfig.z,
      dimensions: { width_px: blockConfig.width || 0, height_px: blockConfig.height || 0 },
      type,
      htmlContent: content,
      sectionId: sectionId,
    };

    return createSocketPromise(socketClient, BlockEvents.BLOCK_ADDED, data, callback);
  }
);

export const addGridTableBlockWithSocket = createAsyncThunk(
  'editor-grid-block/saveContentWithSocket',
  async ({
    content,
    blockConfig,
    socketClient,
    userId,
    callback,
    type,
    sectionId,
  }: {
    content: TableType;
    blockConfig: Block;
    socketClient: SocketClient;
    userId: number;
    callback: GridAddedSocketCallbackType;
    type: GridBlockType;
    sectionId: string;
  }) => {
    const data: GridAddTableType = {
      blockId: uuidv4(),
      userId,
      position: { top_px: blockConfig.y, left_px: blockConfig.x },
      zIndex: blockConfig.z,
      dimensions: { width_px: blockConfig.width || 0, height_px: blockConfig.height || 0 },
      type,
      content,
      sectionId: sectionId,
    };

    return createSocketPromise(socketClient, BlockEvents.BLOCK_ADDED, data, callback);
  }
);

export const saveContentWithSocket = createAsyncThunk(
  'editor-grid-block/saveContentWithSocket',
  async ({
    blockId,
    content,
    socketClient,
    userId,
    callback,
  }: {
    blockId: string;
    content: string;
    socketClient: SocketClient;
    userId: number;
    callback: GridDefaultSocketCallbackType;
  }) => {
    const data: GridContentChangedType = {
      blockId: blockId,
      htmlContent: content,
      userId,
    };

    return createSocketPromise(socketClient, BlockEvents.BLOCK_CONTENT_CHANGED, data, callback);
  }
);

export const saveContentObjectWithSocket = createAsyncThunk(
  'editor-grid-block/saveContentWithSocket',
  async ({
    blockId,
    content,
    socketClient,
    userId,
    callback,
  }: {
    blockId: string;
    content: Record<string, unknown>;
    socketClient: SocketClient;
    userId: number;
    callback: GridDefaultSocketCallbackType;
  }) => {
    const data: GridContentObjectChangedType = {
      blockId: blockId,
      content,
      userId,
    };

    return createSocketPromise(socketClient, BlockEvents.BLOCK_CONTENT_OBJECT_CHANGED, data, callback);
  }
);

export const saveTableContentWithSocket = createAsyncThunk(
  'editor-grid-block/saveContentWithSocket',
  async ({
    blockId,
    content,
    socketClient,
    callback,
  }: {
    blockId: string;
    content: TableType;
    socketClient: SocketClient;
    callback: GridDefaultSocketCallbackType;
  }) => {
    const data: SaveTableContentType = { blockId: blockId, ...content };
    assertCrmRowMarkersValid(data.rows);

    return createSocketPromise(socketClient, BlockEvents.BLOCK_TABLE_CONTENT_CHANGED, data, callback);
  }
);

export const saveLayerWithSocket = createAsyncThunk(
  'editor-grid-block/saveLayerWithSocket',
  async ({ gridBlockId, userId, zIndex, socketClient }: SaveLayerWithSocketPayloadType) => {
    const data: GridLayerChangedType = {
      blockId: gridBlockId,
      userId,
      zIndex,
    };

    return createSocketPromise(socketClient, BlockEvents.BLOCK_LAYER_CHANGED, data, () => true);
  }
);

export const saveConfigWithSocket = createAsyncThunk(
  'editor-grid-block/saveConfigWithSocket',
  async ({
    blockId,
    x,
    y,
    width,
    height,
    eventType,
    userId,
    socketClient,
    callback,
  }: {
    sectionId: string;
    blockId: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    eventType: BlockEvents;
    userId: number;
    socketClient: SocketClient;
    callback: GridDefaultSocketCallbackType;
  }) => {
    if (eventType === BlockEvents.BLOCK_POSITION_CHANGED) {
      const data: GridPositionChangedType = {
        blockId: blockId,
        userId,
        position: { top_px: y || 0, left_px: x || 0 },
      };

      return new Promise<void>((resolve) => {
        socketClient.publish(BlockEvents.BLOCK_POSITION_CHANGED, data, (response: DefaultSocketResponseType) => {
          callback(response);
          resolve();
        });
      });
    } else {
      const data: GridDimensionChangedType = {
        blockId: blockId,
        userId,
        dimensions: { width_px: width || 0, height_px: height || 0 },
      };

      return new Promise<void>((resolve) => {
        socketClient.publish(BlockEvents.BLOCK_DIMENSION_CHANGED, data, (response: DefaultSocketResponseType) => {
          callback(response);
          resolve();
        });
      });
    }
  }
);

/**
 * Simple socket publish that returns response without callbacks
 * Use this in Commands for better control
 */
const createSocketPromiseSimple = <T>(
  socketClient: SocketClient,
  event: BlockEvents | SignatureEvents | SectionEvents,
  data: any
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    socketClient.publish(event, data, (response: any) => {
      if (response.status === AcknowledgmentResponseStatus.OK) {
        resolve(response);
      } else {
        reject(new Error(response.errorCode || 'Socket request failed'));
      }
    });
  });

/**
 * Add block without Redux updates (for Commands)
 */
export const addBlockViaSocket = async (socketClient: SocketClient, payload: GridNewAddType): Promise<GridAddedSocketResponseType> => {
  return createSocketPromiseSimple<GridAddedSocketResponseType>(socketClient, BlockEvents.BLOCK_ADDED, payload);
};

export const updateBlockStylesViaSocket = async (
  socketClient: SocketClient,
  payload: {
    blockId: string;
    userId: number;
    styles: Partial<BlockSettings>;
  }
): Promise<DefaultSocketResponseType> => {
  const data = {
    blockId: payload.blockId,
    updatedByUserId: payload.userId,
    ...payload.styles,
  };

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, BlockEvents.BLOCK_SETTINGS_SAVE, data);
};

/**
 * Delete block without Redux updates (for Commands)
 */
export const deleteBlockViaSocket = async (
  socketClient: SocketClient,
  payload: {
    blockId: string;
  }
): Promise<DefaultSocketResponseType> => {
  const data: GridDeleteType = {
    blockId: payload.blockId,
  };

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, BlockEvents.BLOCK_DELETED, data);
};

/**
 * Update block position without Redux updates (for Commands)
 */
export const updateBlockPositionViaSocket = async (
  socketClient: SocketClient,
  payload: {
    blockId: string;
    userId: number;
    position: { top_px: number; left_px: number };
  }
): Promise<DefaultSocketResponseType> => {
  const data: GridPositionChangedType = {
    blockId: payload.blockId,
    userId: payload.userId,
    position: payload.position,
  };

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, BlockEvents.BLOCK_POSITION_CHANGED, data);
};

/**
 * Update block dimensions without Redux updates (for Commands)
 */
export const updateBlockDimensionsViaSocket = async (
  socketClient: SocketClient,
  payload: {
    blockId: string;
    userId: number;
    dimensions: { width_px: number; height_px: number };
  }
): Promise<DefaultSocketResponseType> => {
  const data: GridDimensionChangedType = {
    blockId: payload.blockId,
    userId: payload.userId,
    dimensions: payload.dimensions,
  };

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, BlockEvents.BLOCK_DIMENSION_CHANGED, data);
};

/**
 * Update block layer (z-index) without Redux updates (for Commands)
 */
export const updateBlockLayerViaSocket = async (
  socketClient: SocketClient,
  payload: {
    blockId: string;
    userId: number;
    zIndex: number;
  }
): Promise<DefaultSocketResponseType> => {
  const data: GridLayerChangedType = {
    blockId: payload.blockId,
    userId: payload.userId,
    zIndex: payload.zIndex,
  };

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, BlockEvents.BLOCK_LAYER_CHANGED, data);
};

/**
 * Update block content object without Redux updates (for Commands)
 */
export const updateContentObjectViaSocket = async (
  socketClient: SocketClient,
  payload: {
    blockId: string;
    userId: number;
    content: Record<string, unknown>;
  }
): Promise<DefaultSocketResponseType> => {
  const data: GridContentObjectChangedType = {
    blockId: payload.blockId,
    content: payload.content,
    userId: payload.userId,
  };

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, BlockEvents.BLOCK_CONTENT_OBJECT_CHANGED, data);
};

/**
 * Update signature content without Redux updates (for Commands)
 */
export const updateSignatureContentViaSocket = async (
  socketClient: SocketClient,
  payload: SignaturePropertyUpdate
): Promise<DefaultSocketResponseType> => {
  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, SignatureEvents.SIGNATURE_UPDATE, payload);
};

export const reassignSignatureSigneeViaSocket = async (
  socketClient: SocketClient,
  payload: SignatureReassignPayload
): Promise<DefaultSocketResponseType> => {
  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, SignatureEvents.SIGNATURE_REASSIGN_SIGNEE, payload);
};

export function buildBlockAddedPayload(params: {
  blockId: string;
  userId: number;
  type: GridBlockType.TEXT | GridBlockType.SHAPE | GridBlockType.IMAGE | GridBlockType.VIDEO;
  htmlContent: string;
  position: BlockPosition;
  dimensions: BlockDimension;
  sectionId: string;
  zIndex?: number;
  content?: ContentType;
  styles?: BlockSettings;
}): BlockAddedData {
  const payload: BlockAddedData = {
    blockId: params.blockId,
    sectionId: params.sectionId,
    userId: params.userId,
    type: params.type,
    htmlContent: params.htmlContent,
    position: params.position,
    dimensions: params.dimensions,
  };

  if (params.sectionId !== undefined) {
    payload.sectionId = params.sectionId;
  }

  if (params.zIndex !== undefined) {
    payload.zIndex = params.zIndex;
  }

  if (params.content !== undefined) {
    payload.content = params.content;
  }

  if (params.styles !== undefined) {
    payload.styles = params.styles;
  }

  return payload;
}

/**
 * Build payload for block:added event (TABLE blocks)
 */
export function buildBlockTableAddedPayload(params: {
  blockId: string;
  userId: number;
  content: TableType;
  position: BlockPosition;
  dimensions: BlockDimension;
  sectionId: string;
  zIndex?: number;
}): BlockTableAddedData {
  const payload: BlockTableAddedData = {
    blockId: params.blockId,
    sectionId: params.sectionId,
    userId: params.userId,
    type: GridBlockType.TABLE,
    content: {
      rows: params.content.rows,
      columns: params.content.columns,
      metadata: params.content.metadata,
      totalRows: params.content.totalRows,
    },
    position: params.position,
    dimensions: params.dimensions,
  };

  if (params.sectionId !== undefined) {
    payload.sectionId = params.sectionId;
  }

  if (params.zIndex !== undefined) {
    payload.zIndex = params.zIndex;
  }

  return payload;
}

export function buildBlockEmbedAddedPayload(params: {
  blockId: string;
  userId: number;
  sectionId: string;
  position: BlockPosition;
  dimensions: BlockDimension;
  zIndex?: number;
  content?: Record<string, never> | null;
  styles?: BlockSettings;
}): BlockEmbedAddedData {
  const payload: BlockEmbedAddedData = {
    blockId: params.blockId,
    sectionId: params.sectionId,
    userId: params.userId,
    type: GridBlockType.EMBED,
    position: params.position,
    dimensions: params.dimensions,
  };
  if (params.zIndex !== undefined) payload.zIndex = params.zIndex;
  if (params.content !== undefined) payload.content = params.content;
  if (params.styles !== undefined) payload.styles = params.styles;
  return payload;
}

export const addSignatureViaSocket = async (
  socketClient: SocketClient,
  sectionId: string,
  signature: SignatureBox
): Promise<DefaultSocketResponseType> => {
  const { assignedSignee } = signature;

  // CRM contacts not yet matched to a real signee record have no uid, but the
  // SIGNATURE_ADD schema requires one whenever assignedSignee is present. Insert
  // unassigned (always accepted) and reassign right after, since
  // SIGNATURE_REASSIGN_SIGNEE only needs { id, type }, no uid required.
  if (assignedSignee && !assignedSignee.uid) {
    await createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, SignatureEvents.SIGNATURE_ADD, {
      ...signature,
      assignedSignee: null,
      sectionId,
    });

    return reassignSignatureSigneeViaSocket(socketClient, {
      signatureBoxId: signature.signatureBoxId,
      assignedSignee: sanitizeAssignedSigneeForReassign(assignedSignee),
    });
  }

  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, SignatureEvents.SIGNATURE_ADD, {
    ...signature,
    assignedSignee: sanitizeAssignedSignee(assignedSignee),
    sectionId,
  });
};

export const deleteSignatureViaSocket = async (socketClient: SocketClient, signature: SignatureBox): Promise<DefaultSocketResponseType> => {
  return createSocketPromiseSimple<DefaultSocketResponseType>(socketClient, SignatureEvents.SIGNATURE_DELETE, {
    ...signature,
    assignedSignee: sanitizeAssignedSignee(signature.assignedSignee),
  });
};

export const addSectionViaSocket = async (
  socketClient: SocketClient,
  payload: {
    id: string;
    title: string;
    afterSectionId?: string;
  }
): Promise<DefaultSocketResponseType> => {
  return createSocketPromiseSimple(socketClient, SectionEvents.SECTION_ADD, payload);
};

export const deleteSectionViaSocket = async (
  socketClient: SocketClient,
  payload: { sectionId: string }
): Promise<DefaultSocketResponseType> => {
  return createSocketPromiseSimple(socketClient, SectionEvents.SECTION_DELETE, payload);
};

export interface ImportSectionsSocketResponse {
  status: AcknowledgmentResponseStatus;
  errorCode: ErrorCode;
  content: {
    sections: SectionContentType[];
  };
}

export const importSectionsViaSocket = (
  socketClient: SocketClient,
  payload: { sourceContentIds: string[]; afterSectionId?: string }
): Promise<ImportSectionsSocketResponse> =>
  createSocketPromiseSimple<ImportSectionsSocketResponse>(socketClient, SectionEvents.SECTION_IMPORTED, payload);

export const updateSectionViaSocket = async (
  socketClient: SocketClient,
  payload: { id: string; title?: string; moveAfterSectionId?: string }
): Promise<DefaultSocketResponseType> => {
  return createSocketPromiseSimple(socketClient, SectionEvents.SECTION_CHANGED, payload);
};
